/**
 * O conteúdo do painel do dado pela origem do bloco clicado (E203.F2, ADR-0146 §2–§4 e §10): cada
 * origem abre o caminho de correção que já existe na mesa (cadastro, protocolo, código declarado,
 * leitura da ficha, revisões, conclusão da CCB, conferência no portal) e, onde ainda não há caminho
 * (a leitura do comprovante de crédito, o grau e o fato do risco), diz o que fazer enquanto isso.
 * A prova e o método explicam e oferecem a saída.
 */
import { ConferenciaNoPortal } from '@/components/ConferenciaNoPortal/ConferenciaNoPortal';
import { ConclusaoDaCcb } from '@/components/MesaEletronica/ConclusaoDaCcb';
import { ptBR } from '@/i18n/pt-BR';
import type { TipoDaOrigem } from '@/lib/documentoDaSaida';
import { CHAVE_DO_EIXO } from '@/lib/revisaoDoDado';

import { CadastroNoPainel } from './CadastroNoPainel';
import { CampoLidoNoPainel } from './CampoLidoNoPainel';
import { CodigoNoPainel } from './CodigoNoPainel';
import { CreditoNoPainel } from './CreditoNoPainel';
import { CustodiaNoPainel } from './CustodiaNoPainel';
import { ExplicacaoDoDado, type SaidaDoPainel } from './ExplicacaoDoDado';
import { InstanteNoPainel } from './InstanteNoPainel';
import { LinhaNoPainel } from './LinhaNoPainel';
import { ModalidadeNoPainel } from './ModalidadeNoPainel';
import { ProtocoloNoPainel } from './ProtocoloNoPainel';
import { RequisitoNoPainel } from './RequisitoNoPainel';
import { RevisaoNoPainel } from './RevisaoNoPainel';
import { RiscoNoPainel } from './RiscoNoPainel';
import { ValorNoPainel } from './ValorNoPainel';
import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado;

const EXPLICA_A_PROVA: Record<string, { texto: string; saidas: SaidaDoPainel[] }> = {
  custodia: { texto: t.explica.custodia, saidas: ['documentos'] },
  registro: { texto: t.explica.registro, saidas: [] },
  qr: { texto: t.explica.qr, saidas: [] },
  mapa: { texto: t.explica.mapa, saidas: ['limitacao'] },
  fotografias: { texto: t.explica.fotografias, saidas: ['fotografias'] },
  fecho: { texto: t.explica.fecho, saidas: ['conclusao'] },
  emissao: { texto: t.explica.emissao, saidas: [] },
};

function daIdentidade(props: PropsDoConteudo): boolean {
  return props.pedido.origem.linha?.startsWith('identidade:') ?? false;
}

function Declarado(props: PropsDoConteudo): JSX.Element {
  const { chave } = props.pedido.origem;
  if (chave === 'cadastro') {
    return (
      <>
        <CadastroNoPainel {...props} />
        {daIdentidade(props) ? <LinhaNoPainel {...props} /> : null}
      </>
    );
  }
  if (chave === 'instante') return <InstanteNoPainel {...props} />;
  // E222.F1: a modalidade da operação, aberta pela trava do perfil normativo
  if (chave === 'modalidade') return <ModalidadeNoPainel {...props} />;
  if (chave === 'protocolo') return <ProtocoloNoPainel {...props} />;
  if (chave === 'codigo_declarado') {
    // E211.F2: a linha do documento revisa a Integridade e o Vínculo; o código declarado abaixo
    if (!props.pedido.origem.linha?.startsWith('custodia:')) return <CodigoNoPainel {...props} />;
    return (
      <>
        <CustodiaNoPainel {...props} />
        <div className="border-t border-border pt-sc-3">
          <CodigoNoPainel {...props} />
        </div>
      </>
    );
  }
  return <ExplicacaoDoDado texto={t.instituicao} {...props} />;
}

function Conclusao(props: PropsDoConteudo): JSX.Element {
  const { client, caseId, mesa, onMudou } = props;
  if (mesa.ccbs === null || mesa.ccbs.length === 0) {
    return <ExplicacaoDoDado texto={t.revisao.semPonto} saidas={['conclusao']} {...props} />;
  }
  return (
    <ConclusaoDaCcb
      client={client}
      caseId={caseId}
      pecas={mesa.ccbs}
      todasConcluidas={mesa.todasConcluidas}
      acaoPorCcb={mesa.acaoPorCcb}
      erroPorCcb={mesa.erroPorCcb}
      sucessoPorCcb={mesa.sucessoPorCcb}
      onConcluir={(doc, conclusao) => {
        props.acoes.salvo();
        mesa.concluir(doc, conclusao);
      }}
      onReabrir={mesa.reabrir}
      onMudou={onMudou}
    />
  );
}

/** A linha de valor livre (E211.F4): a rede e as referências da geolocalização. */
function doValor(props: PropsDoConteudo): boolean {
  const linha = props.pedido.origem.linha ?? '';
  return linha.startsWith('rede:') || linha.startsWith('geolocalizacao:');
}

/** O dado lido e, na linha da conferência de identidade, o resultado da linha (E203.F4); na
 * linha da rede do signatário, o valor do perito (E211.F4). */
function Lido(props: PropsDoConteudo): JSX.Element {
  return (
    <>
      <CampoLidoNoPainel {...props} />
      {daIdentidade(props) ? <LinhaNoPainel {...props} /> : null}
      {doValor(props) ? <ValorNoPainel {...props} /> : null}
    </>
  );
}

function Situacao(props: PropsDoConteudo): JSX.Element {
  const { origem } = props.pedido;
  // o requisito com revisão formal (face, autorização) segue pela aba Pendências
  if (origem.revisao !== null) return <RevisaoNoPainel tipo={origem.revisao} {...props} />;
  if (daIdentidade(props)) return <LinhaNoPainel {...props} />;
  // E211.F3: o requisito da matriz e o eixo de geolocalização se revisam aqui, com nota
  if (origem.linha?.startsWith('matriz:')) return <RequisitoNoPainel {...props} />;
  if (origem.linha === CHAVE_DO_EIXO) return <LinhaNoPainel {...props} texto={t.eixo.texto} />;
  if (doValor(props)) return <ValorNoPainel {...props} />; // E211.F4
  const texto = origem.linha !== null ? t.situacao.linha : t.situacao.resultado;
  return <ExplicacaoDoDado texto={texto} comNota {...props} />;
}

function Facial(props: PropsDoConteudo): JSX.Element {
  return (
    <>
      <RevisaoNoPainel tipo="face" {...props} />
      <ExplicacaoDoDado texto={t.explica.fotografias} saidas={['fotografias']} {...props} />
    </>
  );
}

function Prova(props: PropsDoConteudo): JSX.Element {
  const prova = EXPLICA_A_PROVA[props.pedido.origem.chave] ?? {
    texto: t.explica.metodo,
    saidas: [],
  };
  return <ExplicacaoDoDado texto={prova.texto} saidas={prova.saidas} comNota {...props} />;
}

const POR_TIPO: Record<TipoDaOrigem, (props: PropsDoConteudo) => JSX.Element> = {
  declarado: Declarado,
  lido: Lido,
  // E207.F7 (owner, 01/11): a linha do crédito abre pelo resultado, como as linhas de identidade;
  // E207.F7.C01: um formulário só (`FormularioDoCredito`), os pontos ficam na aba Pendências.
  credito: CreditoNoPainel,
  situacao: Situacao,
  conclusao: Conclusao,
  facial: Facial,
  portal: (props) => (
    <ConferenciaNoPortal client={props.client} caseId={props.caseId} onRegistrada={props.onMudou} />
  ),
  risco: RiscoNoPainel,
  prova: Prova,
  catalogo: (props) => <ExplicacaoDoDado texto={t.explica.metodo} comNota {...props} />,
};

export function ConteudoDoDado(props: PropsDoConteudo): JSX.Element {
  const Conteudo = POR_TIPO[props.pedido.origem.tipo];
  return <Conteudo {...props} />;
}
